import * as XLSX from "xlsx";

import { UploadExcelResult } from "api/data";

type NamingSourceData = Pick<UploadExcelResult, "unnamedColumns">;

/**
 * Returns the flagged unnamed column indices, ascending. Each value is the index into
 * rawDataColumns. The backend only flags data-bearing columns, so every index is a real
 * column the user can name.
 */
export function deriveUnnamedColumnItems(data: NamingSourceData): number[] {
  const unnamedColumns = data.unnamedColumns ?? [];
  return [...unnamedColumns].sort((a, b) => a - b);
}

type ExistingNamesSourceData = Pick<
  UploadExcelResult,
  "columns" | "rawDataColumns" | "uwiHeaderName"
>;

/**
 * Existing post-dedup column names the naming step must not collide with: response data columns,
 * non-empty rawDataColumns headers (covers dedup suffix forms like "Header (1)"), and the
 * resolved key header.
 */
export function collectExistingColumnNames(data: ExistingNamesSourceData): string[] {
  const names = new Set<string>();

  data.columns?.forEach((column) => {
    if (column.name) names.add(column.name);
  });
  data.rawDataColumns?.forEach((column) => {
    if (column.headerName) names.add(column.headerName);
  });
  if (data.uwiHeaderName) names.add(data.uwiHeaderName);

  return Array.from(names);
}

/**
 * Returns a validation message when `name` collides case-insensitively with an existing
 * response column name or with another non-blank draft in the same naming step; returns null when
 * the name is valid OR blank (blank simply means "drop this column", not an error).
 */
export function getColumnNameError(
  name: string,
  index: number,
  drafts: Record<number, string>,
  existingNames: string[]
): Nullable<string> {
  const trimmed = name.trim();
  if (!trimmed) return null;

  const lower = trimmed.toLowerCase();

  if (existingNames.some((existing) => existing.toLowerCase() === lower)) {
    return "A column with this name already exists.";
  }

  const collidesWithSibling = Object.entries(drafts).some(
    ([draftIndexStr, draftValue]) => {
      if (Number(draftIndexStr) === index) return false;
      const draftTrimmed = (draftValue ?? "").trim();
      return draftTrimmed.length > 0 && draftTrimmed.toLowerCase() === lower;
    }
  );

  if (collidesWithSibling) {
    return "Another unnamed column already uses this name.";
  }

  return null;
}

// The backend silently de-duplicates identical headers by suffixing " (N)" (e.g. a second
// "Value" becomes "Value (1)"), so a file with duplicate headers arrives as distinct names
// and no plain duplicate is visible. This matches the backend's rename form: a "<base> (N)"
// name whose <base> is also present in the same set.
const DEDUP_ARTIFACT = /^(.+?) \(\d+\)$/;

/**
 * Strips the backend's duplicate-header suffix (" (1)", " (2)", …) for display, so a
 * de-duplicated column reads as its plain base name (e.g. "Value (1)" → "Value") rather than
 * showing a confusing counter. Names without the suffix are returned trimmed, unchanged.
 */
export function stripDedupSuffix(name: string): string {
  const trimmed = (name ?? "").trim();
  const match = trimmed.match(DEDUP_ARTIFACT);
  return match ? match[1].trim() : trimmed;
}

/**
 * Counts the number of duplicate *groups* (pairs) in the list: columns that collapse to the
 * same base name form one group. A backend "<base> (N)" suffix is only collapsed onto its
 * base when that base is actually present, matching findConflictingNameIndices — so
 * "Value" + "Value (1)" is one pair, but two genuinely distinct "Report (1)" / "Report (2)"
 * headers (with no bare "Report") are NOT a pair. Used for the "N pair(s)" count.
 */
export function countDuplicateGroups(names: string[]): number {
  const present = new Set(
    names.map((name) => (name ?? "").trim().toLowerCase()).filter(Boolean)
  );
  const canonicalize = (name: string): string => {
    const trimmed = (name ?? "").trim();
    const match = trimmed.match(DEDUP_ARTIFACT);
    if (match && present.has(match[1].trim().toLowerCase())) {
      return match[1].trim().toLowerCase();
    }
    return trimmed.toLowerCase();
  };
  const counts = new Map<string, number>();
  names.forEach((name) => {
    const canonical = canonicalize(name);
    if (!canonical) return;
    counts.set(canonical, (counts.get(canonical) ?? 0) + 1);
  });
  let groups = 0;
  counts.forEach((count) => {
    if (count > 1) groups += 1;
  });
  return groups;
}

/**
 * Returns the positions (indices into `names`) of every column that participates in a name
 * conflict — BOTH sides, not just one: an exact case-insensitive repeat flags all copies, and
 * a backend "<base> (N)" rename flags the suffixed column AND its base column(s). Blank names
 * are ignored. Used to tint every offending header cell.
 */
export function findConflictingNameIndices(names: string[]): number[] {
  const trimmed = names.map((name) => (name ?? "").trim());
  const byLower = new Map<string, number[]>();
  trimmed.forEach((name, index) => {
    if (!name) return;
    const lower = name.toLowerCase();
    const bucket = byLower.get(lower) ?? [];
    bucket.push(index);
    byLower.set(lower, bucket);
  });

  const flagged = new Set<number>();
  // Exact duplicates: every column sharing a name.
  byLower.forEach((indices) => {
    if (indices.length > 1) indices.forEach((index) => flagged.add(index));
  });
  // Backend "<base> (N)" renames: the suffixed column and its base column(s).
  trimmed.forEach((name, index) => {
    const match = name.match(DEDUP_ARTIFACT);
    if (!match) return;
    const baseIndices = byLower.get(match[1].trim().toLowerCase());
    if (baseIndices) {
      flagged.add(index);
      baseIndices.forEach((baseIndex) => flagged.add(baseIndex));
    }
  });

  return Array.from(flagged).sort((a, b) => a - b);
}

function bookTypeFromFileName(fileName: string): XLSX.BookType {
  const extension = fileName.split(".").pop()?.toLowerCase();
  if (extension === "xls") return "xls";
  if (extension === "csv") return "csv";
  return "xlsx";
}

/**
 * Reads a File as an ArrayBuffer via FileReader rather than File.prototype.arrayBuffer(),
 * which jsdom (the Jest test environment) does not implement.
 */
function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Rewrites blank header cells of a client-held workbook file in memory and returns a new
 * File with the same name, so naming an unnamed column routes through the existing
 * re-upload path and gets server-side normalization rather than being constructed
 * client-side from raw data. Blank/whitespace-only rename drafts are left untouched
 * (unnamed, dropped on Proceed).
 */
export async function renameWorkbookHeaders(
  file: File,
  headerRowIndex: number,
  renames: Record<number, string>
): Promise<File> {
  const buffer = await readFileAsArrayBuffer(file);
  // Read number formats, styles, and dates so rewriting only the header cells doesn't strip
  // formatting off the data cells. Without cellNF/cellDates a formatted date cell (serial +
  // number format) is re-serialized as a bare serial number and reaches the backend corrupted.
  const workbook = XLSX.read(buffer, {
    type: "array",
    cellNF: true,
    cellStyles: true,
    cellDates: true
  });
  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");

  Object.entries(renames).forEach(([indexStr, rawName]) => {
    const trimmed = (rawName ?? "").trim();
    if (!trimmed) return;

    const columnIndex = Number(indexStr);
    const address = XLSX.utils.encode_cell({
      c: range.s.c + columnIndex,
      r: range.s.r + headerRowIndex
    });
    sheet[address] = { t: "s", v: trimmed };
  });

  const bookType = bookTypeFromFileName(file.name);
  // Keep the number formats/styles read above so they survive the round-trip.
  const output = XLSX.write(workbook, { bookType, type: "array", cellStyles: true });

  return new File([output], file.name, { type: file.type });
}
